import { Tracking } from "@/tracking/Tracking";
import { postUrlEncoded } from "../LegacyAxiosUtil";
import I18Next from "./../../Control/Vue/I18Next";
import { prompt } from "./../../Control/Vue/Notifications/Prompts";
import { Step } from "./Step";
import { StepFactory } from "./StepFactory";
import { StepType } from "./StepType";

export class StepWizard extends ProgressIndicator {
	private readonly stepWizard: JQuery;
	private readonly loaderControl: JQuery;
	private readonly steppedControl: JQuery;
	private readonly form: JQuery;
	private readonly steps: Step[] = new Array<Step>();

	private wizardData: Record<string, unknown> = {};
	private loading = false;

	constructor(containerId: DOMElementIdentifier, private constructionParameters: ConstructionParameters) {
		super(jQuery("#" + containerId)[0]);

		jQuery(window).bind("pageshow", () => {
			this.reloadPageIfNavigatedBackOrForward();
		});

		this.stepWizard = jQuery("#" + containerId);

		this.loaderControl = jQuery(`#${this.constructionParameters.loadingControlId}`);

		this.steppedControl = jQuery(`#${this.constructionParameters.steppedControlId}`);
		this.form = jQuery("form");

		jQuery(document).ready(this.initialize.bind(this));
	}

	/**
	 * To make sure that the wizard is always up to date we reload the page
	 * if the member navigated to the page with a wizard using browser navigation buttons.
	 *
	 * This is not ideal as the browser will start to load the cached page
	 * and then immediately reload the page whether it is needed or not.
	 * The user will experience a blink of the page but that is much better
	 * than allowing the user to work with the not up to date wizard.
	 *
	 * E.g. In the registration wizard "Check out" step, the member selects with PayPal payment option.
	 * After redirect to PayPal the member changes his/her mind and hits back browser button.
	 * The member lands on the registration wizard once more. Without this page reload the member
	 * would land at the first "Create child" step although the account has been already created.
	 */
	private reloadPageIfNavigatedBackOrForward() {
		if (window.performance
			&& window.performance.navigation
			&& window.performance.navigation.type === PerformanceNavigation.TYPE_BACK_FORWARD) {
			window.location.reload();
		}
	}

	private initialize() {
		this.showProgressIndicator(true, "div", true);

		const hasRegisterParentStep = this.stepWizard.data("has-register-parent-step") === true;
		Tracking.TrackRegistrationStarted(
			hasRegisterParentStep,
			this.stepWizard.data("tracking-product-type"),
		);

		this.steppedControl.steps({
			bodyTag: this.stepWizard.data("body-tag"),
			headerTag: this.stepWizard.data("header-tag"),
			onFinished: this.onFinished.bind(this),
			onFinishing: this.onFinishing.bind(this),
			onInit: this.onInit.bind(this),
			onStepChanged: this.onStepChanged.bind(this),
			// We use @types/jquery-steps but a different implementation which allows promises onStepChanging
			// therefore Promise<boolean> is actually the correct type but the typing file does not know.
			onStepChanging: this.onStepChanging.bind(this) as any,
			titleTemplate: "<div class=\"title\">#title#</div><span class=\"number\">#index#</span>",
		});
		this.setUpForm();
	}

	private setUpForm() {
		this.form.validate().settings.errorPlacement = (error: JQuery, element: JQuery) => {
			// ConfigureChild places its error container uniquely, so we check for that first. Selector after that is for every other input.
			element.closest(".mia-ConfigureChild").find(".mia-formBlock .error-container")
				.add(element.closest(".form-group").find(".error-container"))
				.append(error);
		};

		// allow validation of hidden elements
		this.form.validate().settings.ignore = ":hidden:not(.body.current #grade)";

		this.steps.forEach((step) => step.setUpValidation(this.form.validate().settings.rules));

		this.form.keypress(this.onFormKeypress.bind(this));
	}

	private onInit(_event: string, currentIndex: number) {
		this.initializeSteps();
		this.hideProgressIndicator();
		this.loaderControl.hide();
		this.showWizad();
		this.steps.forEach((step) => step.initialize());
		this.steps[currentIndex].onStepLoad();
		this.enableNextStep();
		this.updatePreviousButtonVisibility(currentIndex);
	}

	private initializeSteps() {
		const bodyTag = this.stepWizard.data("body-tag");
		const stepControls = this.stepWizard.find(bodyTag);
		jQuery.each(stepControls, (_index: number, step: any) => {
			const stepType = jQuery(step).data("step");
			this.steps.push(StepFactory.GetStep(stepType, jQuery(step)));
		});
	}

	private showWizad() {
		this.steppedControl.css({
			position: "static",
			visibility: "visible",
		});
	}

	private enableNextStep() {
		const currentStep = this.steppedControl.find(".steps > ul > li.current");
		currentStep.next("li").removeClass("disabled");
	}

	private onStepChanging(_event: string, currentIndex: number, newIndex: number): Promise<boolean> {
		// Always allow previous action even if the current form is not valid!
		if (currentIndex > newIndex) {
			return Promise.resolve(true);
		}

		// Sync guard: Bootstrap button("loading") is applied asynchronously, and the Next
		// control is an <a>, so disabled alone does not stop rapid clicks.
		if (this.loading || !this.form.valid()) {
			return Promise.resolve(false);
		}

		this.loading = true;
		return this.changeToNextStep(currentIndex, newIndex);
	}

	private async changeToNextStep(currentIndex: number, newIndex: number): Promise<boolean> {
		const nextLink = this.getNextLink();
		nextLink.button("loading");

		try {
			this.fillWizardInWithCurrentStepData(currentIndex, newIndex);
			const status = await this.steps[currentIndex].onStepChanging(this.steps[newIndex].Type);
			if (status) {
				this.steps[currentIndex].trackCompletedStep();
			}
			return status;
		}
		finally {
			nextLink.button("reset");
			this.loading = false;
		}
	}

	private onStepChanged(_event: string, currentIndex: number, _priorIndex: number) {
		this.steps[currentIndex].onStepLoad();
		this.enableNextStep();
		this.updatePreviousButtonVisibility(currentIndex);
		this.updateNextButtonTextForCheckout(currentIndex);
	}

	private fillWizardInWithCurrentStepData(currentIndex: number, newIndex: number) {
		this.steps[currentIndex].fillInData(this.wizardData);
		this.steps[currentIndex].setWizardData(this.wizardData);
		this.steps[newIndex].setWizardData(this.wizardData);
	}

	private onFinishing(_event: string, _currentIndex: number): boolean {
		return this.form.valid();
	}

	private onFinished(_event: string, currentIndex: number) {
		if (this.loading) {
			return;
		}

		this.loading = true;
		this.steps[currentIndex].trackCompletedStep();
		void this.finishWizard(currentIndex);
	}

	private async finishWizard(currentIndex: number): Promise<void> {
		const finishLink = this.getFinishLink();
		finishLink.button("loading");
		this.showProgressIndicator(true, `${this.constructionParameters.bodyTag}.current`, true);

		const requestData = this.wizardData;
		this.steps[currentIndex].fillInData(requestData);

		const finishUrl = this.stepWizard.data("finish-url");
		try {
			const { data } = await postUrlEncoded<any>(finishUrl, requestData);

			if (data.success && data.redirectUrl) {
				window.location.href = data.redirectUrl;
			}
			else if (data.errorMessage) {
				this.showErrorMessage(data.errorMessage);
			}
		}
		finally {
			finishLink.button("reset");
			this.loading = false;
			this.hideProgressIndicator();
		}
	}

	private onFormKeypress(event: JQuery.Event) {
		if (event.which === 13) {
			const nextLink = this.getNextLink();
			if (nextLink.is(":visible")) {
				nextLink.click();
				return;
			}

			const finishLink = this.getFinishLink();
			if (finishLink.is(":visible")) {
				finishLink.click();
			}
		}
	}

	private getNextLink(): JQuery {
		return this.stepWizard.find("a[href=\"#next\"]");
	}

	private getFinishLink(): JQuery {
		return this.stepWizard.find("a[href=\"#finish\"]");
	}

	private showErrorMessage(errorMessage: string) {
		prompt(errorMessage, [{ label: I18Next.tr("OkCancel_Ok"), value: undefined }]);
	}

	/**
	 * Uses direct DOM manipulation since jQuery Steps lacks a proper API.
	 * Hides the button on the first step, shows it otherwise.
	 */
	private updatePreviousButtonVisibility(currentIndex: number) {
		const prevLink = jQuery("a[href=\"#previous\"]");
		prevLink.css("display", currentIndex === 0 ? "none" : "inline-block");
	}

	/**
	 * If the wizard was rendered with a custom checkout button text key, swap the button text of the
	 * wizard's last action to that key's translation, so it is labeled accurately. That last action is
	 * the Next button leading into the "BuyMembership" step for flows whose checkout completes outside
	 * the wizard (e.g. a redirect to an external checkout site), or the Finish button for flows without
	 * a checkout step (e.g. state funded). Otherwise keep the default labels.
	 */
	private async updateNextButtonTextForCheckout(currentIndex: number) {
		const checkoutNextButtonTextKey: unknown = this.stepWizard.data("checkout-next-button-text-key");
		if (typeof checkoutNextButtonTextKey !== "string") {
			return;
		}

		const nextLink = this.getNextLink();
		const isNextStepCheckout = this.steps[currentIndex + 1]?.Type === StepType.BuyMembership;
		if (nextLink.length > 0 && isNextStepCheckout) {
			nextLink.text(await I18Next.tr(checkoutNextButtonTextKey));
		}

		const finishLink = this.getFinishLink();
		const hasCheckoutStep = this.steps.some((step) => step.Type === StepType.BuyMembership);
		const isFinishRelabeled = !hasCheckoutStep && currentIndex === this.steps.length - 1;
		if (finishLink.length > 0 && isFinishRelabeled) {
			finishLink.text(await I18Next.tr(checkoutNextButtonTextKey));
		}

		this.steppedControl.toggleClass("is-custom-checkout-next", isNextStepCheckout || isFinishRelabeled);
	}
}
